import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { contentAPI } from '../../services/api';
import { formatMarathiDate, formatMarathiNumber } from '../../utils/marathi';
import {
  GALLERY_CATEGORY_OPTIONS,
  getGalleryCategory,
  getGalleryPreviewUrl,
  getGalleryVideoEmbedUrl,
  groupGalleryItems,
  normalizeGalleryItem,
  sortGalleryItems,
} from '../../utils/gallery';
import './GalleryPage.css';

const MEDIA_FILTERS = [
  { value: 'all', label: 'सर्व मीडिया' },
  { value: 'image', label: 'छायाचित्रे' },
  { value: 'video', label: 'व्हिडिओ' },
];

const SORT_OPTIONS = [
  { value: 'curated', label: 'निवडक क्रम' },
  { value: 'newest', label: 'नवीन प्रथम' },
  { value: 'oldest', label: 'जुने प्रथम' },
  { value: 'title', label: 'शीर्षकानुसार' },
];

function GalleryThumbnail({ item }) {
  const [failed, setFailed] = useState(false);
  const previewUrl = getGalleryPreviewUrl(item);

  if (failed) return <div className="gallery-media-fallback"><span>{item.media_type === 'video' ? '▶' : '▧'}</span><small>पूर्वदृश्य उपलब्ध नाही</small></div>;
  if (previewUrl) return <img src={previewUrl} alt={item.alt_text || item.title} loading="lazy" onError={() => setFailed(true)} />;
  if (item.media_type === 'video') return <video src={item.image_url} muted playsInline preload="metadata" aria-hidden="true" onError={() => setFailed(true)} />;
  return <div className="gallery-media-fallback"><span>▧</span><small>प्रतिमा उपलब्ध नाही</small></div>;
}

function GalleryCard({ item, onOpen }) {
  return (
    <button
      type="button"
      className={`gallery-media-card${item.is_featured ? ' featured' : ''}`}
      onClick={() => onOpen(item)}
      aria-label={`${item.media_type === 'video' ? 'व्हिडिओ' : 'छायाचित्र'} पहा: ${item.title}`}
    >
      <GalleryThumbnail item={item} />
      <span className="gallery-media-shade" />
      <span className={`gallery-media-type ${item.media_type}`} aria-hidden="true">
        {item.media_type === 'video' ? '▶ व्हिडिओ' : '▧ फोटो'}
      </span>
      {item.is_featured && <span className="gallery-featured-mark">निवडक</span>}
      <span className="gallery-media-copy">
        <small>{getGalleryCategory(item.category).label}{item.event_date ? ` · ${formatMarathiDate(item.event_date, { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}</small>
        <strong>{item.title}</strong>
        {item.caption && <em>{item.caption}</em>}
      </span>
      <span className="gallery-open-mark" aria-hidden="true">↗</span>
    </button>
  );
}

export default function GalleryPage() {
  const { tenantSlug } = useParams();
  const { tenantData, loading: tenantLoading } = useTenant();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mediaFilter, setMediaFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('curated');
  const [viewerIndex, setViewerIndex] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    contentAPI.getGallery(tenantSlug)
      .then(response => {
        if (active) setGallery(response.data.images || []);
      })
      .catch(() => {
        if (!active) return;
        setGallery(tenantData?.gallery || []);
        setError('संपूर्ण गॅलरी लोड झाली नाही. उपलब्ध मीडिया दाखवला आहे.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [tenantSlug, tenantData?.gallery]);

  const items = useMemo(() => gallery.map(normalizeGalleryItem), [gallery]);
  const counts = useMemo(() => ({
    all: items.length,
    image: items.filter(item => item.media_type === 'image').length,
    video: items.filter(item => item.media_type === 'video').length,
    categories: new Set(items.map(item => item.category)).size,
  }), [items]);

  const mediaItems = useMemo(() => mediaFilter === 'all'
    ? items
    : items.filter(item => item.media_type === mediaFilter), [items, mediaFilter]);

  const categoryCounts = useMemo(() => GALLERY_CATEGORY_OPTIONS.reduce((result, category) => ({
    ...result,
    [category.value]: mediaItems.filter(item => item.category === category.value).length,
  }), {}), [mediaItems]);

  const visibleItems = useMemo(() => sortGalleryItems(
    categoryFilter === 'all' ? mediaItems : mediaItems.filter(item => item.category === categoryFilter),
    sortOrder,
  ), [mediaItems, categoryFilter, sortOrder]);

  const groups = useMemo(() => categoryFilter === 'all'
    ? groupGalleryItems(visibleItems)
    : [{ ...getGalleryCategory(categoryFilter), items: visibleItems }], [categoryFilter, visibleItems]);

  const activeItem = viewerIndex === null ? null : visibleItems[viewerIndex];
  const openItem = item => setViewerIndex(visibleItems.findIndex(candidate => candidate.id === item.id));
  const closeViewer = useCallback(() => setViewerIndex(null), []);
  const moveViewer = useCallback(direction => setViewerIndex(index => {
    if (index === null || visibleItems.length < 2) return index;
    return (index + direction + visibleItems.length) % visibleItems.length;
  }), [visibleItems.length]);

  useEffect(() => {
    if (viewerIndex === null) return undefined;
    const onKeyDown = event => {
      if (event.key === 'Escape') closeViewer();
      if (event.key === 'ArrowLeft') moveViewer(-1);
      if (event.key === 'ArrowRight') moveViewer(1);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [closeViewer, moveViewer, viewerIndex]);

  useEffect(() => { setViewerIndex(null); }, [mediaFilter, categoryFilter, sortOrder]);

  if ((tenantLoading && !tenantData) || loading) {
    return <div className="gallery-page"><div className="gallery-loading"><span /><strong>गॅलरी तयार होत आहे…</strong></div></div>;
  }

  return (
    <div className="gallery-page">
      <section className="gallery-hero">
        <div className="gallery-hero-copy">
          <span>ग्रामपंचायत दृश्य संग्रह</span>
          <h1>फोटो आणि व्हिडिओ गॅलरी</h1>
          <p>गावातील विकासकामे, ग्रामसभा, कार्यक्रम आणि दैनंदिन जीवनाचा वर्गवारीनुसार संग्रह.</p>
        </div>
        <div className="gallery-hero-stats" aria-label="गॅलरी आकडेवारी">
          <div><strong>{formatMarathiNumber(counts.image)}</strong><span>फोटो</span></div>
          <div><strong>{formatMarathiNumber(counts.video)}</strong><span>व्हिडिओ</span></div>
          <div><strong>{formatMarathiNumber(counts.categories)}</strong><span>वर्ग</span></div>
        </div>
      </section>

      {error && <div className="gallery-error" role="status">{error}</div>}

      <section className="gallery-controls" aria-label="गॅलरी फिल्टर">
        <div className="gallery-media-tabs" role="tablist" aria-label="मीडिया प्रकार">
          {MEDIA_FILTERS.map(filter => (
            <button
              key={filter.value}
              type="button"
              role="tab"
              aria-selected={mediaFilter === filter.value}
              className={mediaFilter === filter.value ? 'active' : ''}
              onClick={() => { setMediaFilter(filter.value); setCategoryFilter('all'); }}
            >
              {filter.label}<span>{formatMarathiNumber(counts[filter.value])}</span>
            </button>
          ))}
        </div>

        <div className="gallery-category-row">
          <button type="button" className={categoryFilter === 'all' ? 'active' : ''} onClick={() => setCategoryFilter('all')}>
            सर्व वर्ग <span>{formatMarathiNumber(mediaItems.length)}</span>
          </button>
          {GALLERY_CATEGORY_OPTIONS.map(category => categoryCounts[category.value] > 0 && (
            <button key={category.value} type="button" className={categoryFilter === category.value ? 'active' : ''} onClick={() => setCategoryFilter(category.value)}>
              {category.label} <span>{formatMarathiNumber(categoryCounts[category.value])}</span>
            </button>
          ))}
        </div>

        <div className="gallery-sort-row">
          <p><strong>{formatMarathiNumber(visibleItems.length)}</strong> मीडिया नोंदी</p>
          <label>क्रमवारी<select value={sortOrder} onChange={event => setSortOrder(event.target.value)}>{SORT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
        </div>
      </section>

      {visibleItems.length === 0 ? (
        <section className="gallery-empty">
          <span aria-hidden="true">▧</span>
          <h2>या विभागात मीडिया उपलब्ध नाही</h2>
          <p>दुसरा वर्ग किंवा मीडिया प्रकार निवडा.</p>
          <button type="button" onClick={() => { setMediaFilter('all'); setCategoryFilter('all'); }}>संपूर्ण गॅलरी पहा</button>
        </section>
      ) : (
        <div className="gallery-collections">
          {groups.map(group => (
            <section className="gallery-collection" key={group.value}>
              <header><div><span>संग्रह</span><h2>{group.label}</h2></div><p>{formatMarathiNumber(group.items.length)} नोंदी</p></header>
              <div className="gallery-media-grid">
                {group.items.map(item => <GalleryCard key={item.id} item={item} onOpen={openItem} />)}
              </div>
            </section>
          ))}
        </div>
      )}

      {activeItem && (
        <div className="gallery-viewer" role="presentation" onClick={closeViewer}>
          <div className="gallery-viewer-dialog" role="dialog" aria-modal="true" aria-label={activeItem.title} onClick={event => event.stopPropagation()}>
            <div className="gallery-viewer-media">
              <button className="gallery-viewer-close" type="button" onClick={closeViewer} aria-label="बंद करा">×</button>
              {visibleItems.length > 1 && <button className="gallery-viewer-prev" type="button" onClick={() => moveViewer(-1)} aria-label="मागील मीडिया">‹</button>}
              {activeItem.media_type === 'image' ? (
                <img src={activeItem.image_url} alt={activeItem.alt_text || activeItem.title} />
              ) : getGalleryVideoEmbedUrl(activeItem) ? (
                <iframe src={getGalleryVideoEmbedUrl(activeItem)} title={activeItem.title} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
              ) : (
                <video key={activeItem.id} src={activeItem.image_url} poster={activeItem.thumbnail_url || undefined} controls autoPlay playsInline />
              )}
              {visibleItems.length > 1 && <button className="gallery-viewer-next" type="button" onClick={() => moveViewer(1)} aria-label="पुढील मीडिया">›</button>}
            </div>
            <div className="gallery-viewer-info">
              <div className="gallery-viewer-meta"><span>{getGalleryCategory(activeItem.category).label}</span><span>{activeItem.media_type === 'video' ? 'व्हिडिओ' : 'छायाचित्र'}</span>{activeItem.event_date && <span>{formatMarathiDate(activeItem.event_date, { day: 'numeric', month: 'long', year: 'numeric' })}</span>}</div>
              <h2>{activeItem.title}</h2>
              {activeItem.caption && <p>{activeItem.caption}</p>}
              <small>{formatMarathiNumber(viewerIndex + 1)} / {formatMarathiNumber(visibleItems.length)}</small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
